/* global process */ // only process.env.PUBLIC_URL, which vite.config.mjs `define` replaces at build time
import axios from 'axios';
import { onApiFailure, onApiSuccess } from 'src/utils/connectivityDetector';
import { CLINICAL_RECORDS_CHANGED } from 'src/util/clinicalRecordEvents';

const instance = axios.create({
  baseURL: `${process.env.PUBLIC_URL || ''}/api`
});

// Session-expiry handling. The authenticated app shell registers a handler that
// clears client auth state, shows a message and redirects to the login page.
// When no handler is registered (e.g. public questionnaire pages) we do nothing
// and let the rejection propagate.
let unauthorizedHandler = null;
let lastUnauthorizedAt = 0;

export function setUnauthorizedHandler(fn) {
  unauthorizedHandler = fn;
}

// Login/logout-flow endpoints: a 401 here means bad credentials / an already-
// gone session, not "your active session just expired" — so don't redirect.
const AUTH_FLOW_PATHS = ['/login', '/token', '/signout', '/sso'];
const isAuthFlowRequest = (url = '') =>
  AUTH_FLOW_PATHS.some((p) => String(url).includes(p));

// Signing out of a session that has already ended is the one auth-flow 401
// that still means "go to the sign-in page" (TGCLOUD-1855). The Log out button
// and the SSO inactivity sign-out only `.then` the call, and relied on the
// reload below to leave the screen, which a 401 no longer triggers.
const SIGN_OUT_PATHS = ['/signout', '/sso-signout'];
const isSignOutRequest = (url = '') =>
  SIGN_OUT_PATHS.some((p) => String(url).includes(p));

// The body of a security-group read-only refusal (backend util/util.js).
export const READ_ONLY_ACCESS_MESSAGE = 'Read-only access';

function handleUnauthorized() {
  // Debounce: a burst of parallel requests can all 401 at once (polls,
  // heartbeats) — only act on the first.
  const ts = Date.now();
  if (ts - lastUnauthorizedAt < 3000) return;
  lastUnauthorizedAt = ts;
  if (typeof unauthorizedHandler === 'function') unauthorizedHandler();
}

// Every successful write to a clinical record is announced, so whatever shows
// what is outstanding (the home notification feed, TGCLOUD-1896) reads again
// instead of waiting for a reload.
const CLINICAL_RECORD_URL = /^\/?clinical-notes\//;

function announceClinicalRecordWrite(config) {
  const method = String(config?.method || 'get').toLowerCase();
  if (
    method === 'get' ||
    !CLINICAL_RECORD_URL.test(String(config?.url || ''))
  ) {
    return;
  }
  try {
    window.dispatchEvent(new Event(CLINICAL_RECORDS_CHANGED));
  } catch (_) {
    // No window to tell (a test outside jsdom): nothing is listening.
  }
}

instance.interceptors.response.use(
  (response) => {
    if (response.headers['reload-required']) window.location.reload();
    onApiSuccess();
    announceClinicalRecordWrite(response.config);
    return response;
  },
  (err) => {
    const status = err?.response?.status;

    // Hand off to the logout/redirect handler ONLY for a genuine session
    // expiry, which the auth middleware marks with { sessionExpired: true }.
    // An authenticated-but-forbidden response (403, or a stray/legacy 401 from
    // a permission gate) must NOT log the user out — those surface as a normal
    // rejected request. Auth-flow endpoints are skipped too (bad-credentials
    // 401s there are not "your active session just expired").
    const sessionExpired = err?.response?.data?.sessionExpired === true;
    const url = err?.config?.url;
    if (
      status === 401 &&
      sessionExpired &&
      (!isAuthFlowRequest(url) || isSignOutRequest(url))
    ) {
      handleUnauthorized();
      return Promise.reject(err);
    }

    // A read-only or locked workspace (TGCLOUD-1844). The group gate answers
    // 403 { groupAccess }; tell the licence banner, which explains it once,
    // instead of every caller showing "An error has occurred".
    if (status === 403 && err?.response?.data?.groupAccess) {
      try {
        window.dispatchEvent(
          new CustomEvent('tg:group-access', { detail: err.response.data })
        );
      } catch (_) {
        // no window (tests / SSR) — nothing to tell
      }
    }

    // A member of a read-only security group writing anyway (backend
    // util/util.js READ_ONLY_ACCESS, answered by enforce() and by every write on
    // the clinical-record router). Most forms print the error itself —
    // `Upload failed! ${err}` — which for an AxiosError says only "Request
    // failed with status code 403". Carry the server's reason on the error so
    // those existing snacks say why.
    if (
      status === 403 &&
      err?.response?.data?.message === READ_ONLY_ACCESS_MESSAGE
    ) {
      err.message = READ_ONLY_ACCESS_MESSAGE;
    }

    // Role/permission change can be signalled via a header on the response.
    // Never on a 401 (TGCLOUD-1855): a 401 means there is no session, and a
    // reload cannot create one. The backend set the header on every refusal
    // from its session gate, and '/token' skips the logout branch above, so
    // honouring it here reloaded the page before AdminPanel could send the
    // browser to the login page — for ever, for anyone who opened the site
    // without a session.
    if (status !== 401 && err?.response?.headers?.['reload-required'])
      window.location.reload();

    // Count network errors and 5xx as connectivity failures; 4xx is an
    // app-level error and shouldn't trip the offline banner.
    if (!status || status >= 500) {
      onApiFailure();
    } else {
      onApiSuccess();
    }
    return Promise.reject(err);
  }
);

export default instance;
